/**
 * Warna arsiran dan pita lembar log, sebagaimana proyek memilihnya.
 *
 * Daftarnya — kunci apa saja yang ada, namanya, dan bawaannya — **tidak
 * tertulis di sini**. Ia turun dari server bersama nilainya, sebab daftar yang
 * hidup di dua tempat adalah daftar yang cepat atau lambat berselisih, dan
 * selisihnya berupa baris penyunting yang mengubah warna yang tidak digambar
 * siapa pun. Yang ada di berkas ini hanyalah cara membacanya dan cara
 * menuliskannya kembali.
 *
 * Yang tersimpan hanyalah yang berbeda dari bawaannya. Sebuah proyek yang
 * menyimpan seluruh peta warna apa adanya akan membeku pada bawaan versi hari
 * ia dibuka: bawaan yang bergeser di rilis berikutnya tidak akan pernah sampai
 * kepadanya, dan tidak ada satu pun tanda di permukaan yang mengatakan
 * sebabnya.
 */

import { api } from "../api";

export interface LogColorSwatch {
  key: string;
  label: string;
  group: string;
  /** Bawaannya — boleh berupa `var(--…)`, yang ikut berganti pada tema gelap. */
  default: string;
  note: string;
}

export interface LogColorPayload {
  /** Yang berlaku: bawaan, ditimpa yang tersimpan. Berkunci penuh. */
  colors: Record<string, string>;
  /** Yang benar-benar tersimpan di proyek — hanya yang berbeda. */
  stored: Record<string, string>;
  swatches: LogColorSwatch[];
}

export function logColorsUrl(projectId: number | string): string {
  return `/api/projects/${projectId}/log-colors/`;
}

export function readLogColors(projectId: number | string): Promise<LogColorPayload> {
  return api.get<LogColorPayload>(logColorsUrl(projectId));
}

export function writeLogColors(
  projectId: number | string,
  stored: Record<string, string>,
): Promise<LogColorPayload> {
  return api.put<LogColorPayload>(logColorsUrl(projectId), { colors: stored });
}

/**
 * Kelompok dalam urutan kemunculannya, bukan urutan abjad.
 *
 * Urutan server adalah urutan tempat warnanya terlihat di lembar — pita, lalu
 * kelas, lalu arsiran — dan penyunting yang mengurutkannya menurut abjad
 * memisahkan dua baris yang bersebelahan di layar.
 */
export function groupSwatches(
  swatches: LogColorSwatch[],
): { group: string; swatches: LogColorSwatch[] }[] {
  const out: { group: string; swatches: LogColorSwatch[] }[] = [];
  for (const swatch of swatches) {
    const last = out[out.length - 1];
    if (last && last.group === swatch.group) last.swatches.push(swatch);
    else out.push({ group: swatch.group, swatches: [swatch] });
  }
  return out;
}

/**
 * Warna yang dipajang kotaknya bagi satu kunci.
 *
 * Bawaan yang berupa variabel tema tidak bisa dipajang sebagai heks, dan
 * memaksanya menjadi heks berarti membekukannya pada satu tema. Yang
 * dipulangkan karena itu adalah nilainya apa adanya; yang memajangnya
 * menyerahkannya kepada CSS, yang memang tahu cara membacanya.
 */
export function swatchColor(
  payload: LogColorPayload | undefined,
  key: string,
  fallback: string,
): string {
  return payload?.colors[key] || fallback;
}
